import React, { createContext, useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token,   setToken]   = useState(() => localStorage.getItem("user_token"));
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Login ──────────────────────────────────────────────────
  const handleLogin = (token, userData = null) => {
    setToken(token);
    localStorage.setItem("user_token", token);

    try {
      const decoded = jwtDecode(token);

      // Merge decoded JWT + full API response so nothing is lost.
      // adminRole and adminPermissions come from both the JWT payload
      // (set in adminAuthController) and the userData response body.
      const mergedUser = {
        ...decoded,
        ...userData,
        userId:           userData?.userId           || decoded?.userId,
        role:             userData?.role             || decoded?.role,
        username:         userData?.userName         || decoded?.userName,
        email:            userData?.email            || decoded?.email,
        adminRole:        userData?.adminRole        || decoded?.adminRole        || "admin",
        adminPermissions: userData?.adminPermissions || decoded?.adminPermissions || [],
        isActive:         userData?.isActive         ?? decoded?.isActive         ?? true,
      };

      // Persist full user object — this is what we read back on refresh
      localStorage.setItem("user",     JSON.stringify(mergedUser));
      localStorage.setItem("username", mergedUser.username    || "");
      localStorage.setItem("role",     mergedUser.role        || "admin");
      localStorage.setItem("userId",   mergedUser.userId      || "");

      setUser(mergedUser);
      return mergedUser;
    } catch (err) {
      console.error("Failed to decode token:", err);
      return null;
    }
  };

  // ── Logout ─────────────────────────────────────────────────
  const handleLogout = (message = "Logged out successfully") => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("user_token");
    localStorage.removeItem("user");
    localStorage.removeItem("username");
    localStorage.removeItem("role");
    localStorage.removeItem("userId");
    toast.info(message);
  };

  // ── Hydrate on mount ───────────────────────────────────────
  // Critical: read the full stored user object, not just the JWT decode.
  // The JWT decode only contains what was signed at login time.
  // The stored object may have additional fields (adminRole, adminPermissions,
  // profileImage, etc.) that were merged from the API response.
  useEffect(() => {
    const existingToken = localStorage.getItem("user_token");

    if (existingToken) {
      try {
        const decoded = jwtDecode(existingToken);

        if (decoded.exp * 1000 < Date.now()) {
          handleLogout("Session expired. Please login again.");
        } else {
          setToken(existingToken);

          // ── KEY FIX: prefer the full stored user over bare JWT decode ──
          // The stored "user" key has adminRole + adminPermissions from login.
          // The JWT decode alone might not have them if the token is older.
          const storedRaw = localStorage.getItem("user");
          if (storedRaw) {
            try {
              const stored = JSON.parse(storedRaw);
              setUser(stored);
            } catch {
              // Stored object is corrupted — fall back to JWT decode
              setUser(decoded);
            }
          } else {
            setUser(decoded);
          }
        }
      } catch (err) {
        console.error("Invalid token:", err);
        handleLogout("Session expired. Please login again.");
      }
    }

    setLoading(false);
  }, []);

  return (
    <AuthContext.Provider value={{
      token,
      user,
      loading,
      setUser,
      login:  handleLogin,
      logout: handleLogout,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
